import { DecisionReason, InAppMessageAction } from "../../../../core/internal/model/model"
import { InAppMessageView } from "../view/InAppMessageView"
import InAppMessageHiddenStorage from "../../../../core/internal/evaluation/service/inappmessage/eligibility/match/InAppMessageHiddenStorage"
import { Clock } from "../../../../core/internal/util/TimeUtil"
import { UrlHandler } from "../internal/UrlHandler"
import Logger from "../../../../core/internal/logger"
import ObjectUtil from "../../../../core/internal/util/ObjectUtil"

export interface InAppMessageActionHandler {
  supports(action: InAppMessageAction): boolean

  handle(view: InAppMessageView, action: InAppMessageAction): void
}

export class InAppMessageActionHandlerFactory {
  constructor(private readonly handlers: InAppMessageActionHandler[]) {}

  get(action: InAppMessageAction): InAppMessageActionHandler | undefined {
    return this.handlers.find((it) => it.supports(action))
  }
}

export class InAppMessageCloseActionHandler implements InAppMessageActionHandler {
  handle(view: InAppMessageView, action: InAppMessageAction): void {
    view.close()
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "CLOSE"
  }
}

export class InAppMessageLinkActionHandler implements InAppMessageActionHandler {
  constructor(private urlHandler: UrlHandler) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    try {
      if (!action.value) return
      this.urlHandler.open(new URL(action.value))
    } catch (e) {
      Logger.log.error(`Invalid url: ${action.value}`)
    }
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "WEB_LINK"
  }
}

export class InAppMessageLinkAndCloseActionHandler implements InAppMessageActionHandler {
  constructor(private urlHandler: UrlHandler) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    try {
      if (!action.value) return
      this.urlHandler.open(new URL(action.value))
    } catch (e) {
      Logger.log.error(`Invalid url: ${action.value}`)
    } finally {
      /**
       * HTML 인앱 메시지의 사용자 클릭은 iframe 내부에서 발생하므로, 이동을 시작한 문서는 iframe 문서가 된다.
       * view.close()는 iframe을 DOM에서 동기적으로 제거하는데,
       * WebKit은 이동을 시작한 문서가 이미 detach된 navigation을 예외 없이 조용히 폐기한다.
       * (WebKit Location::setLocation 의 frame null 가드. Blink 에는 없어 iOS Safari 에서만 재현된다.)
       * 따라서 URL을 먼저 열고, view를 닫아야 한다.
       */
      view.close()
    }
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "LINK_AND_CLOSE"
  }
}

export class InAppMessageHiddenActionHandler implements InAppMessageActionHandler {
  constructor(private readonly storage: InAppMessageHiddenStorage, private readonly clock: Clock) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    if (view.context.decisionReason === DecisionReason.OVERRIDDEN) {
      view.close()
      return
    }

    const hideDurationMillis = ObjectUtil.requiredNotNullOrUndefined(
      action.hideDurationMillis,
      () => "hideDurationMillis must not be null for HIDDEN action"
    )
    const expireAt = this.clock.currentMillis() + hideDurationMillis
    this.storage.put(view.context.inAppMessage, expireAt)
    view.close()
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "HIDDEN"
  }
}

export class InAppMessageLinkNewWindowActionHandler implements InAppMessageActionHandler {
  constructor(private urlHandler: UrlHandler) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    try {
      if (!action.value) return
      this.urlHandler.open(new URL(action.value))
    } catch (e) {
      Logger.log.error(`Invalid url: ${action.value}`)
    }
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "LINK_NEW_WINDOW"
  }
}

export class InAppMessageLinkNewWindowAndCloseActionHandler implements InAppMessageActionHandler {
  constructor(private urlHandler: UrlHandler) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    try {
      if (!action.value) return
      this.urlHandler.open(new URL(action.value))
    } catch (e) {
      Logger.log.error(`Invalid url: ${action.value}`)
    } finally {
      /**
       * window.open()은 브라우저의 Transient Activation(임시 활성화)을 필요로 한다.
       * HTML 인앱 메시지의 경우 사용자 클릭이 iframe 내부에서 발생하는데,
       * view.close()가 iframe을 DOM에서 제거하면 해당 browsing context가 파괴되어
       * Transient Activation이 무효화되고, 이후 window.open() 호출이 차단된다.
       * 따라서 URL을 먼저 열고, view를 닫아야 한다.
       * @see https://developer.mozilla.org/ko/docs/Glossary/Transient_activation
       */
      view.close()
    }
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "LINK_NEW_WINDOW_AND_CLOSE"
  }
}

export class InAppMessageLinkNewTabActionHandler implements InAppMessageActionHandler {
  constructor(private urlHandler: UrlHandler) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    try {
      if (!action.value) return
      this.urlHandler.open(new URL(action.value))
    } catch (e) {
      Logger.log.error(`Invalid url: ${action.value}`)
    }
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "LINK_NEW_TAB"
  }
}

export class InAppMessageLinkNewTabAndCloseActionHandler implements InAppMessageActionHandler {
  constructor(private urlHandler: UrlHandler) {}

  handle(view: InAppMessageView, action: InAppMessageAction): void {
    try {
      if (!action.value) return
      this.urlHandler.open(new URL(action.value))
    } catch (e) {
      Logger.log.error(`Invalid url: ${action.value}`)
    } finally {
      /**
       * window.open()은 브라우저의 Transient Activation(임시 활성화)을 필요로 한다.
       * HTML 인앱 메시지의 경우 사용자 클릭이 iframe 내부에서 발생하는데,
       * view.close()가 iframe을 DOM에서 제거하면 해당 browsing context가 파괴되어
       * Transient Activation이 무효화되고, 이후 window.open() 호출이 차단된다.
       * 따라서 URL을 먼저 열고, view를 닫아야 한다.
       * @see https://developer.mozilla.org/ko/docs/Glossary/Transient_activation
       */
      view.close()
    }
  }

  supports(action: InAppMessageAction): boolean {
    return action.type === "LINK_NEW_TAB_AND_CLOSE"
  }
}
